import { useEffect, useMemo, useState } from "react";
import { fetchGalleryItems } from "../services/siteService";

function formatCategoryLabel(value) {
  return (
    value
      ?.split("-")
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(" ") || "Gallery"
  );
}

function getImageSource(item) {
  return item.imageData || item.imageUrl || "";
}

export default function GalleryPage() {
  const [lightbox, setLightbox] = useState(null);
  const [activeAlbum, setActiveAlbum] = useState(null);
  const [galleryItems, setGalleryItems] = useState([]);

  useEffect(() => {
    let isMounted = true;

    async function loadGallery() {
      const items = await fetchGalleryItems();
      if (isMounted) {
        setGalleryItems(items);
      }
    }

    loadGallery();

    return () => {
      isMounted = false;
    };
  }, []);

  const imageItems = useMemo(
    () => galleryItems.filter((item) => (item.mediaType || "image") === "image"),
    [galleryItems]
  );

  const videoItems = useMemo(
    () => galleryItems.filter((item) => item.mediaType === "video"),
    [galleryItems]
  );

  const albums = useMemo(() => {
    const groupedAlbums = imageItems.reduce((collection, item) => {
      const key = item.category || "gallery";

      if (!collection[key]) {
        collection[key] = {
          id: key,
          title: formatCategoryLabel(key),
          cover: item,
          items: [],
        };
      }

      collection[key].items.push(item);
      return collection;
    }, {});

    return Object.values(groupedAlbums);
  }, [imageItems]);

  return (
    <div className="min-h-screen bg-white font-sans">
      <div className="relative overflow-hidden bg-[rgb(122,49,125)] text-white">
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage:
              "radial-gradient(circle at 20% 50%, #fff 1px, transparent 1px), radial-gradient(circle at 80% 80%, #fff 1px, transparent 1px)",
            backgroundSize: "30px 30px",
          }}
        />
        <div className="relative z-10 px-6 py-16 text-center">
          <p className="mb-3 text-xs uppercase tracking-[0.3em] text-teal-300">Our Memories</p>
          <h1 className="mb-3 text-4xl font-extrabold md:text-5xl">Gallery</h1>
          <p className="mx-auto max-w-2xl text-sm text-teal-200 md:text-base">
            Open each event album to explore all photos inside, like Sports Day, Children's Day, classroom activities, and more.
          </p>
        </div>
      </div>

      <section className="mx-auto max-w-6xl px-4 py-10">
        <div className="mb-6 flex items-end justify-between gap-4">
          <div>
            <h2 className="text-3xl font-extrabold text-slate-900">Photo Albums</h2>
            <p className="mt-2 text-sm text-slate-500">
              Each card works like a folder. Click one album to see all photos inside that event.
            </p>
          </div>
          <div className="rounded-full bg-teal-50 px-4 py-2 text-sm font-semibold text-teal-700">
            {albums.length} Albums
          </div>
        </div>

        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {albums.map((album) => (
            <article
              key={album.id}
              className="group cursor-pointer overflow-hidden rounded-[26px] border border-slate-100 bg-white shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
              onClick={() => setActiveAlbum(album)}
            >
              {getImageSource(album.cover) ? (
                <div className="relative aspect-[4/3] overflow-hidden bg-slate-100">
                  <img
                    src={getImageSource(album.cover)}
                    alt={album.title}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-slate-950/10 to-transparent" />
                  <div className="absolute left-4 top-4 inline-flex items-center gap-2 rounded-full bg-white/90 px-3 py-1.5 text-xs font-bold uppercase tracking-[0.2em] text-slate-800">
                    <span className="text-lg leading-none">+</span>
                    Folder
                  </div>
                  <div className="absolute inset-x-0 bottom-0 p-4 text-white">
                    <p className="text-2xl font-black drop-shadow">{album.title}</p>
                    <p className="mt-1 text-sm text-slate-200">{album.items.length} photos inside</p>
                  </div>
                </div>
              ) : null}

              <div className="flex items-center justify-between px-5 py-4">
                <div>
                  <p className="font-semibold text-slate-900">{album.title}</p>
                  <p className="mt-1 text-xs uppercase tracking-[0.2em] text-slate-400">
                    Event Album
                  </p>
                </div>
                <span className="rounded-full bg-teal-50 px-4 py-2 text-xs font-bold text-teal-700 transition-colors duration-300 group-hover:bg-teal-700 group-hover:text-white">
                  Open
                </span>
              </div>
            </article>
          ))}
        </div>

        {albums.length === 0 && (
          <div className="py-20 text-center text-gray-400">
            <span className="text-5xl">Gallery</span>
            <p className="mt-3 text-lg font-semibold">No photo albums yet.</p>
          </div>
        )}
      </section>

      <section className="bg-slate-50 py-12">
        <div className="mx-auto max-w-6xl px-4">
          <div className="mb-6">
            <h2 className="text-3xl font-extrabold text-slate-900">Video Gallery</h2>
            <p className="mt-2 text-sm text-slate-500">YouTube videos added by admin appear here in a separate section.</p>
          </div>

          {videoItems.length > 0 ? (
            <div className="grid gap-6 lg:grid-cols-2">
              {videoItems.map((item) => (
                <article key={item.id} className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
                  <div className="aspect-video bg-black">
                    <iframe
                      src={item.embedUrl}
                      title={item.title}
                      className="h-full w-full"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                    ></iframe>
                  </div>
                  <div className="p-5">
                    <p className="text-xs font-semibold uppercase tracking-[0.25em] text-teal-600">
                      {formatCategoryLabel(item.category)}
                    </p>
                    <h3 className="mt-2 text-xl font-bold text-slate-900">{item.title}</h3>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <div className="rounded-3xl border border-dashed border-slate-300 bg-white px-6 py-16 text-center text-slate-400">
              <p className="text-lg font-semibold">No gallery videos yet.</p>
            </div>
          )}
        </div>
      </section>

      {activeAlbum && (
        <div
          className="fixed inset-0 z-50 overflow-y-auto bg-slate-950/80 p-4 backdrop-blur-sm"
          onClick={() => setActiveAlbum(null)}
        >
          <div
            className="mx-auto mt-6 w-full max-w-6xl rounded-[32px] bg-white p-6 shadow-2xl sm:p-8"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="mb-6 flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-teal-600">Album</p>
                <h3 className="mt-2 text-2xl font-black text-slate-900 sm:text-3xl">{activeAlbum.title}</h3>
                <p className="mt-2 text-sm text-slate-500">{activeAlbum.items.length} photos inside this folder</p>
              </div>
              <button
                onClick={() => setActiveAlbum(null)}
                className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-100 text-xl font-bold text-slate-600 transition-colors hover:bg-slate-200"
              >
                x
              </button>
            </div>

            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {activeAlbum.items.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className="group overflow-hidden rounded-[24px] border border-slate-100 bg-slate-50 text-left shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
                  onClick={() => setLightbox(item)}
                >
                  <div className="aspect-[4/3] overflow-hidden bg-slate-100">
                    <img
                      src={getImageSource(item)}
                      alt={item.title}
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-4">
                    <p className="font-semibold text-slate-900">{item.title}</p>
                    <p className="mt-1 text-xs uppercase tracking-[0.2em] text-slate-400">Click to enlarge</p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {lightbox && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/85 p-4" onClick={() => setLightbox(null)}>
          <div
            className="relative w-full max-w-4xl overflow-hidden rounded-2xl bg-white shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <img
              src={getImageSource(lightbox)}
              alt={lightbox.title}
              className="max-h-[75vh] w-full object-contain bg-slate-100"
            />
            <div className="flex items-center justify-between p-5">
              <div>
                <p className="font-bold text-gray-800">{lightbox.title}</p>
                <p className="mt-0.5 text-xs capitalize text-teal-600">{formatCategoryLabel(lightbox.category)}</p>
              </div>
              <button
                onClick={() => setLightbox(null)}
                className="flex h-9 w-9 items-center justify-center rounded-full bg-gray-100 font-bold text-gray-600 transition-colors hover:bg-gray-200"
              >
                x
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
